import * as React from 'react'
import { View, Text, Image, ScrollView, PixelRatio, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import LoginForm from '@screens/login/LoginForm'
import Drawer from '@components/drawer/Drawer'
import SelectRole from './SelectRole'
import ForgetPassword from './ForgetPassword'
import ExhibitorView from './ExhibitorView'
import SimpleAlert from '@components/modal/SimpleAlert'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import Icon from '@components/icon/Icons'
import theme from '@theme/Theme'

import { VERSION_NAME } from '@app/config'

const BASE_LINE_FONT_SIZE = 75
const BASE_LINE_LINE_HEIGHT = 94
const NUMBER_OF_BASE_LINE = 3
const SCREEN_BASE_FOR_SCALE = 320

const LoginScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const [windowSize] = useBreakpoint()

  const xScale = windowSize.width / SCREEN_BASE_FOR_SCALE

  const renderCloseDrawerHeader = () => (
    <CloseContainer onPress={Drawer.close}>
      <Icon name="close" color={theme.colors.primary} size={35} />
    </CloseContainer>
  )

  const openForgottenPassword = () => {
    Drawer.close()
    Drawer.open({
      render: () => (
        <DrawerScrollView>
          <ForgetPassword onBack={Drawer.close} />
        </DrawerScrollView>
      ),
      header: renderCloseDrawerHeader,
      hideTarget: true,
    })
  }

  const onLoginError = (code?: number) => {
    SimpleAlert.open({
      title: i18n.t(`screens.login.form.errorTitle`),
      description: i18n.t(`screens.login.form.errorSubTitle${code === 400 ? 'WrongInfo' : ''}`),
      buttons: [{ label: i18n.t('common.retry'), onClick: () => SimpleAlert.close() }],
    })
  }

  const showDrawerAs = (role: UserRole) => {
    Drawer.open({
      render: () => (
        <DrawerScrollView>
          {role === 'EXPOSANT' ? (
            <ExhibitorView
              onPressForgetPassword={openForgottenPassword}
              onBack={Drawer.close}
              onLoginSuccess={Drawer.close}
              navigation={navigation}
            />
          ) : (
            <LoginForm
              onPressForgetPassword={openForgottenPassword}
              onBack={Drawer.close}
              onLoginSuccess={Drawer.close}
              onLoginError={onLoginError}
            />
          )}
        </DrawerScrollView>
      ),
      header: renderCloseDrawerHeader,
      hideTarget: true,
    })
  }

  const normalizeFont = () => {
    const newSize = BASE_LINE_FONT_SIZE * xScale
    return Math.round(PixelRatio.roundToNearestPixel(newSize))
  }
  const normalizeLineHeight = () => {
    const newSize = (normalizeFont() * BASE_LINE_LINE_HEIGHT) / BASE_LINE_LINE_HEIGHT
    return Math.round(PixelRatio.roundToNearestPixel(newSize))
  }
  const normalizeTop = (position: number) => {
    const lineHeight = normalizeLineHeight()
    const blankHeight = windowSize.height / 2 - NUMBER_OF_BASE_LINE * lineHeight
    const newSize = blankHeight + position * lineHeight * 0.75
    return Math.round(PixelRatio.roundToNearestPixel(newSize))
  }

  const normalizedFont = normalizeFont()
  const normalizedLineHeight = normalizeLineHeight()

  return (
    <MainContainer>
      <Background source={require('@assets/images/login/connexion_background.png')} />
      <Body>
        <TitleView>
          <Accroche fontSize={normalizedFont} lineHeight={normalizedLineHeight} top={normalizeTop(0)} left="-3%">
            {i18n.t('screens.login.baseLine1')}
          </Accroche>
          <Accroche fontSize={normalizedFont} lineHeight={normalizedLineHeight} top={normalizeTop(1)} left="56%">
            {i18n.t('screens.login.baseLine2')}
          </Accroche>
          <Accroche fontSize={normalizedFont} lineHeight={normalizedLineHeight} top={normalizeTop(2)} left="10%">
            {i18n.t('screens.login.baseLine3')}
          </Accroche>
        </TitleView>
        <ActionView>
          <SelectRole onRoleSelected={(role) => showDrawerAs(role)} />
          <VersionName>{i18n.t('common.version', { version: VERSION_NAME })}</VersionName>
        </ActionView>
      </Body>
    </MainContainer>
  )
}

export default LoginScreen

const MainContainer = styled(View)`
  flex: 1;
  background-color: ${(props) => props.theme.colors.primary};
  overflow: hidden;
  padding-top: ${(props) => props.theme.insets.top}px;
  padding-bottom: ${(props) => props.theme.insets.bottom}px;
`
const Background = styled(Image)`
  position: absolute;
  z-index: -1;
  height: 100%;
  width: 100%;
`
const Body = styled(View)`
  flex: 1;
`
const TitleView = styled(View)`
  flex: 1;
`
const Accroche = styled(Text)<{ top?: number; left?: string; fontSize?: number; lineHeight: number }>`
  ${(props) => props.theme.fonts.LoginTitle};
  font-size: ${(props) => props.fontSize}px;
  line-height: ${(props) => props.lineHeight}px;
  color: ${(props) => props.theme.colors.background};
  position: absolute;
  z-index: 1;
  flex: 1;
  align-self: center;
  top: ${(props) => props.top}px;
  left: ${(props) => (props.left ? props.left : undefined)};
  width: 110%;
`
const ActionView = styled(ScrollView)`
  padding: 0 16px 32px;
  flex: 1;
`

const CloseContainer = styled(TouchableOpacity)`
  padding-left: 16px;
  margin-bottom: 30px;
`
const DrawerScrollView = styled(ScrollView)`
  flex: 1;
`

const VersionName = styled(Text)`
  ${(props) => props.theme.fonts.Caption2};
  text-align: center;
  color: ${(props) => props.theme.colors.background};
  margin: 32px 0 6px;
`
